<template>
	<BaseLayout
		:footer-full="true"
		:footnotes="footnotes"
		:content-full-height="true"
		background-color-class="bg-color-white"
	>
		<div class="container">
			<div class="grid-md gutters-300-md">
				<div class="col-8-md col-offset-2-md">
					<h2 class="heading-2 mb-100 mb-200-md"> Contact our Investor Relations team </h2>
					<p>
						For help with your account or any other questions you have, visit our
						<BaseLink class="text-link" :link="helpLink">Help Center</BaseLink> or use the form below.
					</p>
					<p v-if="includeCalendlyModule">
						As a premium account level investor, you will receive priority support from our team when you
						email us, and you have the option to schedule a call with one of our knowledgeable team
						members.<sup>1</sup>
					</p>
					<h5 v-if="!hasSubmitted" class="heading-5 mt-200 mt-200-md mb-100 mb-50-md">Email us</h5>
					<ContactUsForm @submit-success="hasSubmitted = true" />
					<div v-if="includeCalendlyModule" class="mt-200">
						<h2 class="heading-5 mb-50 handle-calendly-margin">Schedule a call</h2>
						<div
							class="calendly-inline-widget"
							data-auto-load="false"
							style="min-width: 320px; height: 1500px"
						></div>
					</div>
				</div>
			</div>
		</div>
	</BaseLayout>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import ContactUsForm from '@components/contact/contact-us-form.vue';
import { Link } from 'types/layout';
import { useHead } from '@unhead/vue';
import { useScriptTag } from '@vueuse/core';
import { useUserStore } from '@stores/user';

const userStore = useUserStore();

const helpLink: Link = { router: 'help-center-landing', target: '_blank' };
const hasSubmitted = ref(false);

const footnotes = computed((): Array<string> => {
	return includeCalendlyModule.value
		? [
				`Call scheduling is subject to the team's availability so we cannot guarantee that you will be able to schedule a call at your desired time.`
			]
		: [];
});

const includeCalendlyModule = computed((): boolean => {
	return userStore.isPremiumUser;
});

function renderCalendlyInlineWidget(): void {
	window.Calendly.initInlineWidget({
		url: 'https://calendly.com/fundrise-investments-team/premium-investor-support-session',
		parentElement: document.querySelector('.calendly-inline-widget')
	});
}

function setup(): void {
	useHead({
		title: 'Contact us'
	});

	if (includeCalendlyModule.value) {
		useScriptTag('https://assets.calendly.com/assets/external/widget.js', () => renderCalendlyInlineWidget(), {
			attrs: { id: 'contact-calendly-widget' }
		});
	}
}
setup();
</script>
<style lang="scss">
@use '../../styles/utilities/respond-to.scss' as *;
.handle-calendly-margin {
	@include respond-to(lg) {
		margin-bottom: -40px;
	}
}
</style>
